import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import * as React from "react";
import { cx } from "../internal/cx.js";
import { formatMinor, describeMinor, formatMinorForEdit } from "./money.js";
const MONEY_CELL_LABELS = {
  empty: "No amount",
  invalid: "Invalid amount",
  invalidShort: "error",
  invalidTitle: "Invalid amount in the data",
  copy: "Copy amount"
};
function attempt(fn) {
  try {
    return fn();
  } catch {
    return null;
  }
}
const MoneyCell = React.forwardRef(function MoneyCell2({
  valueMinor,
  currency,
  locale,
  fractionDigits,
  signStyle = "minus",
  zeroStyle = "number",
  currencyDisplay = "symbol",
  secondary,
  strong,
  copyable,
  onValueCopy,
  labels: labelsProp,
  className,
  ...rest
}, ref) {
  const labels = { ...MONEY_CELL_LABELS, ...labelsProp };
  const spanRef = ref;
  if (valueMinor === null || valueMinor === void 0) {
    return /* @__PURE__ */ jsxs("span", { ref: spanRef, ...rest, className: cx("ev-money", className), "data-empty": "true", children: [
      /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: "—" }),
      /* @__PURE__ */ jsx("span", { className: "ev-visually-hidden", children: labels.empty })
    ] });
  }
  const opts = { currency, locale, signStyle, zeroStyle, currencyDisplay, ...fractionDigits !== void 0 ? { fractionDigits } : {} };
  const text = attempt(() => formatMinor(valueMinor, opts));
  const full = attempt(() => describeMinor(valueMinor, opts));
  if (text === null || full === null) {
    return /* @__PURE__ */ jsxs("span", { ref: spanRef, title: labels.invalidTitle, ...rest, className: cx("ev-money", className), "data-invalid": "true", children: [
      /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: labels.invalidShort }),
      /* @__PURE__ */ jsx("span", { className: "ev-visually-hidden", children: labels.invalid })
    ] });
  }
  let secondaryText = null;
  let secondaryFull = null;
  if (secondary) {
    const sOpts = {
      currency: secondary.currency,
      locale,
      currencyDisplay: secondary.currencyDisplay ?? "symbol",
      ...secondary.fractionDigits !== void 0 ? { fractionDigits: secondary.fractionDigits } : {}
    };
    secondaryText = attempt(() => formatMinor(secondary.valueMinor, { ...sOpts, signStyle }));
    secondaryFull = attempt(() => describeMinor(secondary.valueMinor, sOpts));
  }
  const hasSecondary = secondaryText !== null && secondaryFull !== null;
  const announced = hasSecondary ? `${full}, ${secondaryFull}` : full;
  const visible = hasSecondary ? /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("span", { className: "ev-money__primary", children: text }),
    /* @__PURE__ */ jsx("span", { className: "ev-money__secondary", children: secondaryText })
  ] }) : text;
  const content = announced === text ? visible : /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: visible }),
    /* @__PURE__ */ jsx("span", { className: "ev-visually-hidden", children: announced })
  ] });
  const cls = cx("ev-money", strong && "ev-money--strong", copyable && "ev-money--copy", className);
  const negative = valueMinor < 0 ? "true" : void 0;
  if (copyable) {
    const copyText = attempt(() => formatMinorForEdit(valueMinor, opts)) ?? text;
    const buttonRest = rest;
    return /* @__PURE__ */ jsx(
      "button",
      {
        ref,
        type: "button",
        title: labels.copy,
        ...buttonRest,
        className: cls,
        "data-negative": negative,
        onClick: (event) => {
          buttonRest.onClick?.(event);
          if (event.defaultPrevented) return;
          void navigator.clipboard?.writeText(copyText).then(
            () => onValueCopy?.(copyText),
            () => void 0
          );
        },
        children: content
      }
    );
  }
  return /* @__PURE__ */ jsx("span", { ref: spanRef, ...rest, className: cls, "data-negative": negative, children: content });
});
MoneyCell.displayName = "MoneyCell";
export {
  MoneyCell
};
//# sourceMappingURL=MoneyCell.js.map
